import { computed, Directive, inject } from '@angular/core';

import { MessagesConstants } from '@shared/constants/messages.constants';
import { EventType, PermissionType } from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { AuthService } from '@shared/store/auth/auth.service';
import { EventQuery } from '@shared/store/event/event.query';
import { WorkflowStepType } from '@shared/store/workflow/workflow.const';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';

@Directive({ standalone: true })
export class SiteWorkflowDirective {
  private readonly authService = inject(AuthService);
  private readonly eventQuery = inject(EventQuery);
  private readonly launchDarklyService = inject(LaunchDarklyService);
  private readonly workflowQuery = inject(WorkflowQuery);

  permissions = this.authService.getPermissions({
    userHasSitePermission: PermissionType.PERMISSION_UPDATE_SITES,
    userHasUpdateSiteCostsPermission: PermissionType.PERMISSION_UPDATE_SITE_COSTS,
  });

  addSiteActionTooltip = computed(() =>
    !this.permissions().userHasSitePermission &&
    !this.permissions().userHasUpdateSiteCostsPermission
      ? MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION
      : ''
  );
  addSiteActionTooltips = computed(() => {
    const tooltips: string[] = [];

    tooltips.push(this.addSiteActionTooltip());

    if (this.isWorkflowLocked()) {
      tooltips.push(MessagesConstants.LOCKED_FOR_PERIOD_CLOSE);
    }

    return tooltips.filter(Boolean);
  });
  deleteSiteTooltips = computed(() => {
    const tooltips: string[] = [];

    if (
      !this.permissions().userHasSitePermission ||
      !this.permissions().userHasUpdateSiteCostsPermission
    ) {
      tooltips.push(MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION);
    }

    if (this.isWorkflowLocked()) {
      tooltips.push(MessagesConstants.PATIENT_TRACKER_CLOSED);
    }

    return tooltips.filter(Boolean);
  });
  disabledAddSiteUI = computed(() => {
    if (
      !this.permissions().userHasSitePermission &&
      !this.permissions().userHasUpdateSiteCostsPermission
    ) {
      return true;
    }

    if (this.isWorkflowLocked()) {
      return true;
    }

    if (this.isClosingPanelEnabled()) {
      return !!this.iCloseMonthsProcessing();
    }

    return false;
  });

  private isWorkflowLocked = this.workflowQuery.getLockStatusByWorkflowStepType(
    WorkflowStepType.WF_STEP_MONTH_CLOSE_LOCK_PATIENT_TRACKER
  );

  disabledUpdateSiteCostUI = computed(
    () => !this.permissions().userHasUpdateSiteCostsPermission || this.isWorkflowLocked()
  );
  siteCostActionTooltip = computed(() =>
    !this.permissions().userHasUpdateSiteCostsPermission
      ? MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION
      : ''
  );
  updateSiteCostTooltips = computed(() => {
    const tooltips: string[] = [];

    tooltips.push(this.siteCostActionTooltip());

    if (this.isWorkflowLocked()) {
      tooltips.push(MessagesConstants.PATIENT_TRACKER_CLOSED);
    }

    return tooltips.filter(Boolean);
  });

  private iCloseMonthsProcessing = this.eventQuery.selectProcessingEvent(
    EventType.CLOSE_TRIAL_MONTH
  );
  private isClosingPanelEnabled = this.launchDarklyService.$select(
    (flags) => flags.closing_checklist_toolbar
  );
}
